import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { blogService } from '../../services/blog.service';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaHeart, FaComment, FaClock, FaGlobe, FaLock } from 'react-icons/fa';

const AllBlogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [category, setCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchBlogs();
    }, [filter, category]);

    const fetchBlogs = async () => {
        try {
            setLoading(true);
            const params = {
                status: 'published'  // Only show published blogs
            };

            // Apply visibility filter
            if (filter === 'public') {
                params.isPublic = 'true';
            } else if (filter === 'internal') {
                params.isPublic = 'false';
            }
            // If filter is 'all', don't set isPublic param (show both)

            // Apply category filter
            if (category !== 'all') {
                params.category = category;
            }

            console.log('Fetching blogs with params:', params);
            const data = await blogService.getAllBlogs(params);
            console.log('Received blogs:', data.blogs?.length);

            setBlogs(data.blogs || []);
        } catch (error) {
            console.error('Fetch blogs error:', error);
            toast.error('Failed to fetch blogs');
        } finally {
            setLoading(false);
        }
    };

    const filteredBlogs = searchTerm
        ? blogs.filter(blog =>
            blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            blog.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
            blog.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
        )
        : blogs;

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold">Knowledge Hub</h1>
                <p className="text-gray-600 mt-2">Browse all published blogs and articles</p>
            </div>

            {/* Search & Filter Bar */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Search */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Search</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search blogs..."
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Category Filter */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Category</label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Categories</option>
                            <option value="technology">Technology</option>
                            <option value="business">Business</option>
                            <option value="design">Design</option>
                            <option value="marketing">Marketing</option>
                            <option value="tutorial">Tutorial</option>
                            <option value="news">News</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    {/* Visibility Filter */}
                    <div>
                        <label className="block text-sm font-medium mb-2">Visibility</label>
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Blogs</option>
                            <option value="public">Public Only</option>
                            <option value="internal">Internal Only</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Total Blogs</p>
                    <p className="text-3xl font-bold text-blue-600 mt-2">{filteredBlogs.length}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center gap-2">
                        <FaGlobe className="text-green-600" />
                        <p className="text-gray-500 text-sm">Public Blogs</p>
                    </div>
                    <p className="text-3xl font-bold text-green-600 mt-2">
                        {filteredBlogs.filter(b => b.isPublic).length}
                    </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center gap-2">
                        <FaLock className="text-purple-600" />
                        <p className="text-gray-500 text-sm">Internal Only</p>
                    </div>
                    <p className="text-3xl font-bold text-purple-600 mt-2">
                        {filteredBlogs.filter(b => !b.isPublic).length}
                    </p>
                </div>
            </div>

            {/* Blogs Grid */}
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            ) : filteredBlogs.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <p className="text-gray-500 text-lg">No blogs found</p>
                    <p className="text-gray-400 text-sm mt-2">
                        {filter === 'internal'
                            ? 'No internal blogs have been published yet'
                            : filter === 'public'
                                ? 'No public blogs have been published yet'
                                : 'No blogs have been published yet'}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredBlogs.map((blog) => (
                        <div
                            key={blog._id}
                            className="bg-white rounded-lg shadow hover:shadow-xl transition-shadow cursor-pointer"
                            onClick={() => navigate(`/dashboard/blogs/view/${blog._id}`)}
                        >
                            {/* Featured Image */}
                            {blog.featuredImage && (
                                <img
                                    src={blog.featuredImage}
                                    alt={blog.title}
                                    className="w-full h-48 object-cover rounded-t-lg"
                                    onError={(e) => {
                                        console.log('Image failed to load:', blog.featuredImage);
                                        e.target.style.display = 'none';
                                    }}
                                />
                            )}

                            <div className="p-6">
                                {/* Visibility Badge */}
                                <div className="flex items-center gap-2 mb-3">
                                    {blog.isPublic ? (
                                        <span className="flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                            <FaGlobe /> Public
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                                            <FaLock /> Internal
                                        </span>
                                    )}
                                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full capitalize">
                                        {blog.category}
                                    </span>
                                </div>

                                {/* Title */}
                                <h3 className="text-xl font-bold mb-2 line-clamp-2 hover:text-blue-600">
                                    {blog.title}
                                </h3>

                                {/* Author & Date */}
                                <p className="text-sm text-gray-500 mb-3">
                                    By {blog.authorName} • {new Date(blog.publishedAt || blog.createdAt).toLocaleDateString()}
                                </p>

                                {/* Excerpt */}
                                <p className="text-gray-600 text-sm mb-4 line-clamp-3">{blog.excerpt}</p>

                                {/* Meta Info */}
                                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                                    <span className="flex items-center gap-1">
                                        <FaEye /> {blog.views || 0}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <FaHeart /> {blog.likes?.length || 0}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <FaComment /> {blog.comments?.length || 0}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <FaClock /> {blog.readTime || 5} min
                                    </span>
                                </div>

                                {/* Tags */}
                                {blog.tags && blog.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {blog.tags.slice(0, 3).map((tag, index) => (
                                            <span key={index} className="text-xs bg-gray-100 px-2 py-1 rounded">
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AllBlogs;